import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Symbol } from "../../../../../../../../models/symbol";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  symbolToUpdate?: Symbol;
  title: string;
  modalTitle?: string;
  hasError?: boolean;
  open: boolean;
  isLoading: boolean;
  closeModal: () => void;
  submitSymbol: (symbol: Symbol) => void;
}

const ExceptionLocationCodeModal = ({
  symbolToUpdate,
  title,
  modalTitle,
  hasError,
  open,
  isLoading,
  closeModal,
  submitSymbol,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [symbolName, setSymbolName] = useState<string>("");
  const [symbolPrefix, setSymbolPrefix] = useState<string>("");
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    symbolName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (symbolName) =>
        symbolName ? symbolName !== "" : false
      ),
    symbolPrefix: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (symbolPrefix) =>
        symbolPrefix ? symbolPrefix !== "" : false
      ),
  });

  const clearFields = () => {
    setSymbolPrefix("");
    setSymbolName("");
  };

  const handleValidateSuccess = (symbols?: Symbol): boolean => {
    schema.validateSync(
      {
        symbolName: symbols?.name,
        symbolPrefix: symbols?.prefix,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (symbols?: Symbol) => {
    try {
      return handleValidateSuccess(symbols);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      name: symbolName,
      prefix: symbolPrefix,
      status,
    } as Symbol;

    if (isValid(object)) {
      submitSymbol(object);
    }
  };

  const capitalizeLabel = (label: string) => label.charAt(0).toUpperCase() + label.slice(1);

  useEffect(() => {
    if (symbolToUpdate) {
      setSymbolName(symbolToUpdate.name);
      setSymbolPrefix(symbolToUpdate.prefix);
      setStatus(symbolToUpdate.status);
    }
  }, [symbolToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        name: symbolName,
        prefix: symbolPrefix,
      } as Symbol;

      isValid(object);
    }
  }, [symbolName, symbolPrefix, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="symbolModal"
      open={open}
      handleClose={closeModal}
      title={title}
      dataCyTitle="symbolModalTitle"
      showActionButtons
      actionButtonProps={{
        textSave: symbolToUpdate ? translate("update") : translate("create"),
        dataCySave: "submitChangesButton",
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        onSave: handleSubmit,
        loading: isLoading,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: `${capitalizeLabel(modalTitle || "")} ${translate("status").toLowerCase()}`,
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              name="symbolPrefix"
              placeholder={translate("code")}
              label={translate("code")}
              disabled={isLoading}
              error={!!getErrorByField("symbolPrefix")}
              errorMessage={getErrorByField("symbolPrefix")}
              value={symbolPrefix}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSymbolPrefix(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              name="symbolName"
              placeholder={translate("name")}
              label={translate("name")}
              disabled={isLoading}
              error={!!getErrorByField("symbolName")}
              errorMessage={getErrorByField("symbolName")}
              value={symbolName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSymbolName(e.target.value)}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default ExceptionLocationCodeModal;
